/**
 * Status indicating whether a student will be promoted, demoted, or stay
 */
export type PromotionStatus = 'PROMOTION_ZONE' | 'SAFE' | 'DEMOTION_ZONE';

export interface PromotionStatusConfig {
  label: string;
  description: string;
  color: string;
  bgColor: string;
  darkBgColor: string;
  icon: string;
}

export const PROMOTION_STATUS_CONFIG: Record<PromotionStatus, PromotionStatusConfig> = {
  PROMOTION_ZONE: {
    label: 'Zona de Ascenso',
    description: 'Top 20% - Ascenderás a la siguiente liga',
    color: '#10B981', // green-500
    bgColor: 'bg-green-100',
    darkBgColor: 'dark:bg-green-900/30',
    icon: '⬆️',
  },
  SAFE: {
    label: 'Zona Segura',
    description: 'Permanecerás en esta liga',
    color: '#6B7280', // gray-500
    bgColor: 'bg-gray-100',
    darkBgColor: 'dark:bg-gray-800',
    icon: '➡️',
  },
  DEMOTION_ZONE: {
    label: 'Zona de Descenso',
    description: 'Bottom 15% - Descenderás a la liga anterior',
    color: '#EF4444', // red-500
    bgColor: 'bg-red-100',
    darkBgColor: 'dark:bg-red-900/30',
    icon: '⬇️',
  },
};

export function getPromotionStatusConfig(status: PromotionStatus): PromotionStatusConfig {
  return PROMOTION_STATUS_CONFIG[status];
}

export function getPromotionStatusLabel(status: PromotionStatus): string {
  return PROMOTION_STATUS_CONFIG[status].label;
}

export function getPromotionStatusColor(status: PromotionStatus): string {
  return PROMOTION_STATUS_CONFIG[status].color;
}

export function getPromotionStatusIcon(status: PromotionStatus): string {
  return PROMOTION_STATUS_CONFIG[status].icon;
}

/**
 * Calculate promotion status based on position and total members
 * Top 20% = PROMOTION_ZONE
 * Bottom 15% = DEMOTION_ZONE
 * Rest = SAFE
 */
export function calculatePromotionStatus(
  position: number,
  totalMembers: number,
  isBottomTier: boolean = false
): PromotionStatus {
  if (totalMembers === 0) return 'SAFE';

  const promotionThreshold = Math.ceil(totalMembers * 0.2); // Top 20%
  const demotionThreshold = Math.ceil(totalMembers * 0.15); // Bottom 15%

  if (position <= promotionThreshold) {
    return 'PROMOTION_ZONE';
  }

  // Can't demote from bottom tier (NOVATO)
  if (!isBottomTier && position > totalMembers - demotionThreshold) {
    return 'DEMOTION_ZONE';
  }

  return 'SAFE';
}
